// src/hooks/useEventEligibility.js
// ============================================================================
// EVENT ELIGIBILITY HOOK
// Combined check for all requirements to create/join events:
// - IGN for the selected game
// - CritCash stream key (Mux or legacy relay)
//
// Stream Key Detection (two-tier, matches backend getMyStreamKey logic):
//   CHECK 1: muxStreams/{userId} - Mux CDN streaming (current system)
//   CHECK 2: streamKeys/{streamKey} - Legacy relay streaming
//
// IMPORTANT: Only 'revoked' and 'disabled' statuses mean a key is dead.
// Post-session statuses like 'ended', 'idle' are normal and valid.
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, onSnapshot, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { PLATFORM_SERVICES } from '../constants/platformServices';

/**
 * Hook to check if a user is eligible to create/join events
 * Checks: IGN for game + CritCash stream key
 *
 * Platform lock: pass the event's platform to require an IGN declared for
 * THAT platform (anything other than 'Cross-Platform'). hasIGN then means
 * "has an IGN for this platform"; hasAnyIGN stays game-level so the UI can
 * tell "no profile at all" apart from "profile exists, missing this platform".
 *
 * @param {string} userId - The user's UID
 * @param {string} gameId - The game ID to check IGN for
 * @param {string|null} platform - Event platform to enforce (e.g. 'PS5'); null/'Cross-Platform' = any
 * @returns {object} Eligibility status and details
 */
export function useEventEligibility(userId, gameId, platform = null) {
  // IGN State
  const [hasIGN, setHasIGN] = useState(false);
  const [hasAnyIGN, setHasAnyIGN] = useState(false);
  const [ign, setIgn] = useState(null);
  const [ignLoading, setIgnLoading] = useState(true);

  // Linked platform account state (e.g., PSN ID for GT7)
  const [serviceAccountName, setServiceAccountName] = useState(null);
  const [serviceShortName, setServiceShortName] = useState(null);

  // Stream Key State
  const [hasStreamKey, setHasStreamKey] = useState(false);
  const [streamKeyData, setStreamKeyData] = useState(null);
  const [streamKeyLoading, setStreamKeyLoading] = useState(true);

  // ============================================================================
  // IGN CHECK (realtime subscription)
  // ============================================================================
  useEffect(() => {
    if (!userId || !gameId) {
      setHasIGN(false);
      setHasAnyIGN(false);
      setIgn(null);
      setIgnLoading(false);
      return;
    }

    setIgnLoading(true);
    const profileRef = doc(db, 'users', userId, 'gameProfiles', gameId);

    // Platform-locked events require an IGN declared for that platform.
    const platformLocked = !!platform && String(platform).toLowerCase() !== 'cross-platform';

    const unsubscribe = onSnapshot(profileRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const gameIgn = data.ign?.trim();
        setHasAnyIGN(!!gameIgn && gameIgn.length >= 2);

        let userIgn = gameIgn;
        if (platformLocked) {
          // Mirror backend validateUserGameProfile: platform entry first,
          // legacy single-platform profile if its platform matches, else none.
          userIgn = data.platforms?.[platform]?.ign?.trim()
            || (data.platform === platform ? gameIgn : null);
        }
        setIgn(userIgn || null);
        setHasIGN(!!userIgn && userIgn.length >= 2);

        // Extract linked platform account from platforms data. For locked
        // platforms, that platform's linked account takes priority.
        let svcName = null;
        let svcShort = null;
        if (data.platforms) {
          const entries = platformLocked && data.platforms[platform]
            ? [data.platforms[platform], ...Object.values(data.platforms)]
            : Object.values(data.platforms);
          for (const platformData of entries) {
            if (platformData.serviceId && platformData.serviceAccountName) {
              svcName = platformData.serviceAccountName;
              svcShort = PLATFORM_SERVICES[platformData.serviceId]?.shortName || platformData.serviceId;
              break;
            }
          }
        }
        setServiceAccountName(svcName);
        setServiceShortName(svcShort);
      } else {
        setIgn(null);
        setHasIGN(false);
        setHasAnyIGN(false);
        setServiceAccountName(null);
        setServiceShortName(null);
      }
      setIgnLoading(false);
    }, (error) => {
      console.error('Error in game profile subscription:', error);
      setIgnLoading(false);
    });

    return () => unsubscribe();
  }, [userId, gameId, platform]);

  // ============================================================================
  // STREAM KEY CHECK
  // Two-tier detection matching backend logic (getMyStreamKey, confirmReady):
  //   CHECK 1: muxStreams/{userId} - Current Mux-based streaming
  //   CHECK 2: streamKeys collection - Legacy relay-based streaming
  //
  // IMPORTANT: Only reject explicitly dead statuses (revoked, disabled).
  // Statuses like 'ended', 'idle', 'inactive' are normal post-session states
  // and do NOT mean the key is gone. This matches the backend fix applied to
  // getMyStreamKey in broadcast/index.js.
  // ============================================================================
  const DEAD_STATUSES = ['revoked', 'disabled'];

  const fetchStreamKey = useCallback(async () => {
    if (!userId) {
      setHasStreamKey(false);
      setStreamKeyData(null);
      setStreamKeyLoading(false);
      return;
    }

    setStreamKeyLoading(true);
    try {
      let found = false;

      // ------------------------------------------------------------------
      // CHECK 1: muxStreams/{userId} (Mux CDN streaming - current system)
      // If the user has a Mux live stream configured, they're good to go.
      // ------------------------------------------------------------------
      try {
        const muxDoc = await getDoc(doc(db, 'muxStreams', userId));
        if (muxDoc.exists()) {
          const muxData = muxDoc.data();
          // A valid Mux stream has a muxLiveStreamId (the Mux resource ID)
          if (muxData.muxLiveStreamId) {
            setStreamKeyData({
              hasKey: true,
              source: 'mux',
              streamKey: muxData.muxStreamKey || null,
              muxLiveStreamId: muxData.muxLiveStreamId,
              ...muxData,
            });
            setHasStreamKey(true);
            found = true;
          }
        }
      } catch (muxErr) {
        // muxStreams read failed (permissions, network) - fall through to legacy
        console.warn('muxStreams check failed, trying streamKeys:', muxErr.message);
      }

      // ------------------------------------------------------------------
      // CHECK 2: streamKeys collection (legacy relay-based streaming)
      // Query by userId only - no status filter in the query.
      // Filter client-side: only reject revoked/disabled keys.
      // ------------------------------------------------------------------
      if (!found) {
        const streamKeysRef = collection(db, 'streamKeys');
        const q = query(
          streamKeysRef,
          where('userId', '==', userId),
          limit(5) // Grab a few in case some are revoked
        );

        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          // Find the first non-dead key
          const validDoc = snapshot.docs.find((d) => {
            const status = (d.data().status || 'active').toLowerCase();
            return !DEAD_STATUSES.includes(status);
          });

          if (validDoc) {
            const data = validDoc.data();
            setStreamKeyData({
              hasKey: true,
              source: 'legacy',
              streamKey: validDoc.id, // Document ID is the stream key
              ...data,
            });
            setHasStreamKey(true);
            found = true;
          }
        }
      }

      // ------------------------------------------------------------------
      // No valid stream key found in either system
      // ------------------------------------------------------------------
      if (!found) {
        setStreamKeyData({ hasKey: false });
        setHasStreamKey(false);
      }
    } catch (error) {
      console.error('Error checking stream key:', error);
      setStreamKeyData(null);
      setHasStreamKey(false);
    } finally {
      setStreamKeyLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStreamKey();
  }, [fetchStreamKey]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const isLoading = ignLoading || streamKeyLoading;
  
  const isEligible = useMemo(() => {
    if (isLoading) return false;
    return hasIGN && hasStreamKey;
  }, [isLoading, hasIGN, hasStreamKey]);

  const missingRequirements = useMemo(() => {
    const missing = [];
    if (!ignLoading && !hasIGN) missing.push('ign');
    if (!streamKeyLoading && !hasStreamKey) missing.push('streamKey');
    return missing;
  }, [ignLoading, hasIGN, streamKeyLoading, hasStreamKey]);

  return {
    // Overall eligibility
    isEligible,
    isLoading,
    missingRequirements,
    
    // IGN details (hasIGN is platform-aware when a platform was passed;
    // hasAnyIGN is the game-level check regardless of platform)
    hasIGN,
    hasAnyIGN,
    ign,
    ignLoading,

    // Linked platform account (e.g., PSN: Kreger13 for GT7)
    serviceAccountName,
    serviceShortName,
    
    // Stream key details
    hasStreamKey,
    streamKeyData,
    streamKeyLoading,
    
    // Actions
    refetchStreamKey: fetchStreamKey,
  };
}

export default useEventEligibility;